import React, { FC, useEffect, useState } from 'react';
import { Popup } from '@silverfort/sf-components';
import RiskReportFooter from './RiskReportFooter';
import RiskReportContent from './RiskReportContent/RiskReportContent';
import useSaveRiskReportSettings from '../api/mutations/useSaveRiskReportSettings';
import useRiskReportGeneration from '../api/mutations/useRiskReportGeneration';
import { FREQUENCY_OPTIONS } from '@silverfort-nodelibs/common/utils/consts';
import moment from 'moment';
import { ReportSettings } from '../api/types/RiskReport';

interface Props {
    open: boolean;
    onClose: () => void;
    refreshReportFilesData: () => void;
    settings: ReportSettings | undefined;
}

const RiskReportPopup: FC<Props> = ({ open, onClose, refreshReportFilesData, settings }) => {
    const [frequency, setFrequency] = useState(settings?.frequency || FREQUENCY_OPTIONS.DAILY.value);
    const [dayInWeek, setDayInWeek] = useState<string | undefined>(settings?.dayInWeek);
    const [dayInMonth, setDayInMonth] = useState<string | undefined>(settings?.dayInMonth);
    const [assessmentPeriod, setAssessmentPeriod] = useState<string | undefined>(settings?.assessmentPeriod);
    const [hour, setHour] = useState<string | undefined>(settings?.hour);
    const [timezone, setTimezone] = useState<string | undefined>(settings?.timezone);
    const [recipients, setRecipients] = useState<string[]>(settings?.recipients || []);
    const [isAutomaticallyGenerated, setIsAutomaticallyGenerated] = useState(
        settings?.isAutomaticallyGenerated || false
    );
    const [reportFromDate, setReportFromDate] = useState<moment.Moment | null>(
        settings?.reportFromDate ? moment(settings.reportFromDate) : null
    );
    const [reportToDate, setReportToDate] = useState<moment.Moment | null>(moment());
    const [validationErrors, setValidationErrors] = useState<string | null>(null);

    const {
        mutate: triggerSaveRiskReportSettings,
        isLoading: saveSettingsLoading,
        error: saveSettingsError
    } = useSaveRiskReportSettings();
    const {
        mutate: triggerRiskReportGeneration,
        isLoading: generateLoading,
        isSuccessful: isReportGeneratedSuccessfully
    } = useRiskReportGeneration();

    useEffect(() => {
        if (saveSettingsError) {
            setValidationErrors(saveSettingsError);
        } else {
            setValidationErrors('');
        }
    }, [saveSettingsError]);

    useEffect(() => {
        let errorMessage: string | null = null;
        if (!reportFromDate?.isValid()) {
            errorMessage = 'report from date is not a valid date';
        } else if (reportFromDate?.isAfter(moment())) {
            errorMessage = 'report from date cannot be bigger than today';
        } else if (!reportToDate?.isValid()){
            errorMessage = 'report to date is not a valid date';
        } else if (reportToDate?.isAfter(moment())) {
            errorMessage = 'report to date cannot be bigger than today';
        } else if (reportFromDate && reportToDate && reportFromDate.isAfter(reportToDate)) {
            errorMessage = 'report from date cannot be after report to date';
        }

        setValidationErrors(errorMessage);
    }, [reportFromDate, reportToDate]);

    useEffect(() => {
        setFrequency(settings?.frequency || FREQUENCY_OPTIONS.DAILY.value);
        setDayInWeek(settings?.dayInWeek);
        setDayInMonth(settings?.dayInMonth);
        setAssessmentPeriod(settings?.assessmentPeriod);
        setHour(settings?.hour);
        setTimezone(settings?.timezone);
        setRecipients(settings?.recipients || []);
        setIsAutomaticallyGenerated(settings?.isAutomaticallyGenerated || false);
        setReportFromDate(settings?.reportFromDate ? moment(settings.reportFromDate) : null);
    }, [settings]);

    useEffect(() => {
        if (isReportGeneratedSuccessfully) {
            onClose();
        }
    }, [isReportGeneratedSuccessfully, onClose]);

    const onGenerate = () => {
        triggerRiskReportGeneration({
            frequency,
            dayInWeek,
            dayInMonth,
            hour,
            timezone,
            isAutomaticallyGenerated,
            reportFromDate,
            reportToDate,
            assessmentPeriod,
            startDate: reportFromDate,
            endDate: reportToDate,
            recipients
        });
        onClose();
    };

    const onSaveSettings = async () => {
        await triggerSaveRiskReportSettings(
            {
                frequency,
                dayInWeek,
                dayInMonth,
                hour,
                timezone,
                isAutomaticallyGenerated,
                reportFromDate,
                reportToDate,
                assessmentPeriod,
                recipients,
                startDate: reportFromDate,
                endDate: reportToDate
            },
            refreshReportFilesData
        );

        onClose();
    };

    return (
        <Popup
            open={open}
            title="Generate Risk Report"
            ContentComponent={RiskReportContent}
            ContentComponentProps={{
                frequency,
                setFrequency,
                dayInWeek,
                setDayInWeek,
                dayInMonth,
                setDayInMonth,
                assessmentPeriod,
                setAssessmentPeriod,
                hour,
                setHour,
                timezone,
                setTimezone,
                isAutomaticallyGenerated,
                setIsAutomaticallyGenerated,
                reportFromDate,
                setReportFromDate,
                reportToDate,
                setReportToDate,
                recipients,
                setRecipients
            }}
            FooterComponent={RiskReportFooter}
            FooterComponentProps={{
                generateLoading,
                saveSettingsLoading,
                onGenerate,
                onSaveSettings,
                errors: validationErrors
            }}
            onClose={onClose}
            popupClassName="!w-[480px]"
        />
    );
};

export default RiskReportPopup;
